import { api } from "../api";
import { API_BASE_URL } from "../../env";
import type {
    FirstPrinciplesPublicBundle,
    PublicSubmitAnswersPayload,
    PublicSubmitAnswersResponse,
} from "./first-principles.types";

function getDefaultAudioFilename(audioBlob: Blob) {
    const t = audioBlob.type || "";
    if (t.includes("mp4")) return "answer.m4a";
    if (t.includes("ogg")) return "answer.ogg";
    if (t.includes("webm")) return "answer.webm";
    return "answer.audio";
}

// Client-side cache: cacheKey → { token, streamUrl, expiresAt }
const streamTokenCache = new Map<
    string,
    { token: string; streamUrl: string; expiresAt: number }
>();

export const firstPrinciplesPublicApi = {
    async getBundle(
        invitationCode: string,
    ): Promise<FirstPrinciplesPublicBundle> {
        const { data } = await api.get<FirstPrinciplesPublicBundle>(
            `/first-principles/public/${encodeURIComponent(invitationCode)}/bundle`,
        );
        return data;
    },

    async submitAnswers(
        invitationCode: string,
        payload: PublicSubmitAnswersPayload,
    ): Promise<PublicSubmitAnswersResponse> {
        const { data } = await api.post<PublicSubmitAnswersResponse>(
            `/first-principles/public/${encodeURIComponent(invitationCode)}/submit`,
            payload,
        );
        return data;
    },

    async submitAudioAnswer(args: {
        invitationCode: string;
        questionId: string;
        audioBlob?: Blob;
        transcriptText?: string;
        currentIndex?: number;
        clientUploadId?: string;
    }): Promise<
        | { ok: true; data: PublicSubmitAnswersResponse }
        | { ok: false; status: number; message: string }
    > {
        const {
            invitationCode,
            questionId,
            audioBlob,
            transcriptText,
            currentIndex,
            clientUploadId,
        } = args;

        const fd = new FormData();
        fd.append("question_id", questionId);

        if (clientUploadId) {
            fd.append("client_upload_id", clientUploadId);
        }

        if (typeof currentIndex === "number" && Number.isFinite(currentIndex)) {
            fd.append("current_index", String(currentIndex));
        }

        if (audioBlob) {
            const audioName = getDefaultAudioFilename(audioBlob);
            fd.append("audio", audioBlob, audioName);
        }

        if (typeof transcriptText === "string") {
            const t = transcriptText.trim();
            if (t) {
                const transcriptFile = new File([t], "transcript.txt", {
                    type: "text/plain",
                });
                fd.append("transcript", transcriptFile);
            }
        }

        try {
            const { data } = await api.post<PublicSubmitAnswersResponse>(
                `/first-principles/public/${encodeURIComponent(invitationCode)}/submit-audio`,
                fd,
                { timeout: 300_000 },
            );
            return { ok: true, data };
        } catch (err: any) {
            const status = err?.response?.status ?? 0;
            const payload = err?.response?.data;
            const msg =
                payload?.detail ||
                payload?.message ||
                (err instanceof Error ? err.message : "Network error");
            return { ok: false, status, message: msg };
        }
    },

    /**
     * Get a proxied stream URL for FP audio.
     *
     * 1. POST stream-token (validates invitation code, returns opaque token)
     * 2. Build GET stream URL with the token as query param
     *
     * Cached for 45 minutes (tokens expire at 50 min).
     */
    async getAudioStreamUrl(
        invitationCode: string,
        questionId: string,
        source: "question" | "answer",
    ): Promise<string> {
        const cacheKey = `${invitationCode}:${questionId}:${source}`;

        // Reuse cached token if not expiring in the next 5 min
        const cached = streamTokenCache.get(cacheKey);
        if (cached && cached.expiresAt > Date.now() + 5 * 60_000) {
            return cached.streamUrl;
        }

        const { data } = await api.post<{
            stream_token: string;
            expires_in: number;
        }>(
            `/first-principles/public/${encodeURIComponent(invitationCode)}/audio/stream-token`,
            { question_id: questionId, source },
        );

        const streamUrl =
            `${API_BASE_URL}/first-principles/public/${encodeURIComponent(invitationCode)}/audio/stream/${encodeURIComponent(questionId)}` +
            `?stream_token=${encodeURIComponent(data.stream_token)}&source=${source}`;

        streamTokenCache.set(cacheKey, {
            token: data.stream_token,
            streamUrl,
            expiresAt: Date.now() + data.expires_in * 1000,
        });

        return streamUrl;
    },
};
